import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  LogOut,
  MessageSquare,
  Users,
  ChevronRight,
  Settings,
  Calendar,
  GripVertical,
  Pencil,
  Trash2,
  X,
  Check,
  Building2,
  ClipboardCheck,
  User,
} from "lucide-react";
import { toast } from "sonner";
import FeedbackModal from "./FeedbackModal";

const ChannelsList = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingChannel, setEditingChannel] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [hotelName, setHotelName] = useState("");
  const [hotelFeatures, setHotelFeatures] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);

  const isManager = ["super_admin", "admin", "management"].includes(user?.role);
  const canSeeEvents = isManager || user?.department === "meeting_events";
  const canUseChecklist = ["front_office", "night_audit", "management"].includes(user?.department);
  const showChecklist = hotelFeatures?.checklist && (canUseChecklist || isManager);

  // Check if feedback modal should show
  useEffect(() => {
    const checkFeedback = async () => {
      if (localStorage.getItem(`feedback_dismissed_${user?.id}`)) return;
      try {
        const res = await axios.get(`${API}/feedback/check`);
        if (!res.data.submitted) {
          setShowFeedback(true);
        }
      } catch {}
    };
    checkFeedback();
  }, []);

  // Get hotel name
  useEffect(() => {
    const loadHotelName = async () => {
      if (!user?.selected_hotel) return;
      try {
        const res = await axios.get(`${API}/hotels`);
        const h = res.data.find((h) => h.slug === user.selected_hotel);
        if (h) { setHotelName(h.name); setHotelFeatures(h.features || {}); }
      } catch {}
    };
    loadHotelName();
  }, [user?.selected_hotel]);

  const [dashboardStats, setDashboardStats] = useState(null);

  // Load super admin dashboard
  useEffect(() => {
    return; // Dashboard moved to dedicated /dashboard page
  }, [user?.role]);

  const fetchChannels = async () => {
    try {
      const [channelsRes, orderRes] = await Promise.all([
        axios.get(`${API}/channels`),
        axios.get(`${API}/user/channel-order`),
      ]);

      let chs = channelsRes.data.filter((c) => !c.is_direct && c.channel_type !== "group");
      const savedOrder = orderRes.data.order || [];

      if (savedOrder.length > 0) {
        const orderMap = {};
        savedOrder.forEach((id, idx) => { orderMap[id] = idx; });
        chs.sort((a, b) => {
          const oa = orderMap[a.id] !== undefined ? orderMap[a.id] : 999;
          const ob = orderMap[b.id] !== undefined ? orderMap[b.id] : 999;
          return oa - ob;
        });
      }

      setChannels(chs);
    } catch (error) {
      toast.error("Errore nel caricamento dei canali");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchChannels(); }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const getChannelStyle = (name) => {
    const lower = name.toLowerCase();
    if (lower.includes("front office")) return { icon: "bg-blue-100 text-blue-600", card: "border-blue-200 bg-blue-50/40" };
    if (lower.includes("lost")) return { icon: "bg-amber-100 text-amber-600", card: "border-amber-200 bg-amber-50/40" };
    if (lower.includes("cucina")) return { icon: "bg-orange-100 text-orange-600", card: "border-orange-200 bg-orange-50/40" };
    if (lower.includes("eventi") || lower.includes("gruppi")) return { icon: "bg-purple-100 text-purple-600", card: "border-purple-200 bg-purple-50/40" };
    if (lower.includes("manutenzione")) return { icon: "bg-emerald-100 text-emerald-600", card: "border-emerald-200 bg-emerald-50/40" };
    if (lower.includes("conti aperti")) return { icon: "bg-cyan-100 text-cyan-600", card: "border-cyan-200 bg-cyan-50/40" };
    return { icon: "bg-stone-100 text-stone-600", card: "border-stone-200 bg-white" };
  };

  // Save order to backend
  const saveOrder = async (newChannels) => {
    try {
      await axios.put(`${API}/user/channel-order`, {
        channel_ids: newChannels.map((c) => c.id),
      });
    } catch {}
  };

  // Drag handlers
  const handleDragStart = (idx) => {
    setDraggedIdx(idx);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) return;
    setDragOverIdx(idx);
  };

  const handleDrop = (idx) => {
    if (draggedIdx === null || draggedIdx === idx) {
      setDraggedIdx(null);
      setDragOverIdx(null);
      return;
    }

    const newChannels = [...channels];
    const [moved] = newChannels.splice(draggedIdx, 1);
    newChannels.splice(idx, 0, moved);
    setChannels(newChannels);
    saveOrder(newChannels);
    setDraggedIdx(null);
    setDragOverIdx(null);
    toast.success("Ordine salvato");
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  // Touch drag for mobile
  const touchRef = useRef(null);
  const handleTouchStart = (idx) => {
    touchRef.current = { idx, moved: false };
  };

  // Edit channel
  const startEdit = (ch) => {
    setEditingChannel(ch.id);
    setEditName(ch.name);
    setEditDesc(ch.description);
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) return;
    try {
      await axios.patch(`${API}/channels/${editingChannel}`, {
        name: editName.trim(),
        description: editDesc.trim(),
      });
      toast.success("Canale modificato");
      setEditingChannel(null);
      fetchChannels();
    } catch (error) {
      toast.error("Errore nella modifica");
    }
  };

  // Delete channel
  const handleDelete = async (channelId, channelName) => {
    if (!window.confirm(`Eliminare il canale "${channelName}"?`)) return;
    try {
      await axios.delete(`${API}/channels/${channelId}`);
      toast.success("Canale eliminato");
      fetchChannels();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Move channel up/down for mobile
  const moveChannel = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= channels.length) return;
    const newChannels = [...channels];
    [newChannels[idx], newChannels[newIdx]] = [newChannels[newIdx], newChannels[idx]];
    setChannels(newChannels);
    saveOrder(newChannels);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200">
        <div className="px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-stone-900 tracking-tight">
              End<span className="text-amber-500">•</span>ver
            </h1>
            <p className="text-sm text-stone-500">
              Ciao, <span className="font-medium text-stone-700">{user?.name}</span>
              {user?.selected_hotel && hotelName && (
                <span
                  className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-medium"
                  data-testid="selected-hotel-badge"
                >
                  {hotelName}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {user?.role === "super_admin" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/hotels")}
                className="text-stone-500 hover:text-stone-700 hover:bg-stone-100"
                data-testid="hotel-management-button"
                title="Gestione Hotel"
              >
                <Building2 className="w-5 h-5" />
              </Button>
            )}
            {isManager && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/admin")}
                className="text-stone-500 hover:text-stone-700 hover:bg-stone-100"
                data-testid="admin-settings-button"
              >
                <Settings className="w-5 h-5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-stone-500 hover:text-stone-700 hover:bg-stone-100"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="p-4">
        {/* Super Admin Dashboard */}
        {user?.role === "super_admin" && dashboardStats && (
          <div className="mb-6" data-testid="super-admin-dashboard">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-outfit text-lg font-semibold text-stone-900">Dashboard</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/hotels")}
                className="rounded-full text-xs"
                data-testid="hotel-management-button-2"
              >
                <Building2 className="w-3.5 h-3.5 mr-1.5" />
                Gestione Hotel
              </Button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-stone-200 p-3 text-center">
                <p className="text-2xl font-bold text-stone-900" data-testid="dashboard-hotel-count">{dashboardStats.total_hotels}</p>
                <p className="text-xs text-stone-500">Hotel</p>
              </div>
              <div className="bg-white rounded-xl border border-stone-200 p-3 text-center">
                <p className="text-2xl font-bold text-stone-900" data-testid="dashboard-active-user-count">{dashboardStats.active_users || 0}</p>
                <p className="text-xs text-stone-500">Utenti attivi</p>
              </div>
              <div className="bg-white rounded-xl border border-stone-200 p-3 text-center">
                <p className="text-2xl font-bold text-stone-900" data-testid="dashboard-total-task-count">{dashboardStats.total_tasks}</p>
                <p className="text-xs text-stone-500">Compiti totali</p>
              </div>
              <div className="bg-white rounded-xl border border-amber-200 p-3 text-center bg-amber-50/30">
                <p className="text-2xl font-bold text-amber-700" data-testid="dashboard-open-task-count">{dashboardStats.open_tasks}</p>
                <p className="text-xs text-stone-500">Aperti</p>
              </div>
              <div className="bg-white rounded-xl border border-emerald-200 p-3 text-center bg-emerald-50/30">
                <p className="text-2xl font-bold text-emerald-700" data-testid="dashboard-closed-task-count">{dashboardStats.closed_tasks}</p>
                <p className="text-xs text-stone-500">Chiusi</p>
              </div>
              <div className="bg-white rounded-xl border border-stone-200 p-3 text-center">
                <p className="text-2xl font-bold text-stone-900" data-testid="dashboard-user-count">{dashboardStats.total_users}</p>
                <p className="text-xs text-stone-500">Utenti totali</p>
              </div>
            </div>

            {/* Per-hotel breakdown */}
            {dashboardStats.hotel_stats.length > 0 && (
              <div className="mt-3 space-y-2">
                {dashboardStats.hotel_stats.map((h) => (
                  <div
                    key={h.slug}
                    className="bg-white rounded-xl border border-stone-200 p-3 flex items-center gap-3"
                    data-testid={`dashboard-hotel-${h.slug}`}
                  >
                    <Building2 className="w-5 h-5 text-stone-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-stone-900 text-sm truncate">{h.name}</p>
                      <p className="text-xs text-stone-400">
                        {h.users} utenti · {h.channels} canali · {h.tasks} compiti ({h.closed_tasks} chiusi)
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Events Card */}
        {false && canSeeEvents && (
        <button
          onClick={() => navigate("/events")}
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-4 mb-6 text-left shadow-md hover:shadow-lg transition-all"
          data-testid="events-card"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white">Eventi</h3>
              <p className="text-sm text-white/80">Ordini di servizio</p>
            </div>
            <ChevronRight className="w-5 h-5 text-white/80" />
          </div>
        </button>
        )}

        {/* Check List Card */}
        {false && showChecklist && (
          <button
            onClick={() => navigate("/checklist")}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-4 mb-6 text-left shadow-md hover:shadow-lg transition-all"
            data-testid="checklist-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <ClipboardCheck className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white">Check List</h3>
                <p className="text-sm text-white/80">Turni Front Office</p>
              </div>
              <ChevronRight className="w-5 h-5 text-white/80" />
            </div>
          </button>
        )}

        <div className="flex items-center justify-between mb-4">
          <h2 className="font-outfit text-lg font-semibold text-stone-900">
            Canali
          </h2>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-4 border border-stone-200 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-stone-200" />
                  <div className="flex-1">
                    <div className="h-4 bg-stone-200 rounded w-1/3 mb-2" />
                    <div className="h-3 bg-stone-100 rounded w-2/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : channels.length === 0 ? (
          <div className="text-center py-12 text-stone-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nessun canale disponibile</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="channels-list">
            {channels.map((channel, idx) => {
              const style = getChannelStyle(channel.name);
              const isDirect = channel.is_direct;
              const title = isDirect ? (channel.display_name || "Utente") : channel.name;
              const subtitle = isDirect ? "Messaggio privato" : channel.description;

              // Editing mode
              if (editingChannel === channel.id) {
                return (
                  <div key={channel.id} className="rounded-2xl p-4 border-2 border-blue-300 bg-blue-50/30 space-y-2">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Nome canale"
                      className="font-semibold"
                      autoFocus
                    />
                    <Input
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                      placeholder="Descrizione"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700">
                        <Check className="w-4 h-4 mr-1" /> Salva
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingChannel(null)}>
                        <X className="w-4 h-4 mr-1" /> Annulla
                      </Button>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={channel.id}
                  className={`rounded-2xl border shadow-sm transition-all ${isDirect ? "bg-white border-stone-200" : style.card} ${
                    dragOverIdx === idx ? "ring-2 ring-amber-400" : ""
                  } ${draggedIdx === idx ? "opacity-50" : ""}`}
                  draggable={!isDirect}
                  onDragStart={() => !isDirect && handleDragStart(idx)}
                  onDragOver={(e) => !isDirect && handleDragOver(e, idx)}
                  onDrop={() => !isDirect && handleDrop(idx)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="flex items-center gap-2 p-4">
                    {/* Drag handle (groups/operational only) */}
                    {!isDirect && (
                      <div className="flex flex-col gap-0.5 cursor-grab active:cursor-grabbing text-stone-300 hover:text-stone-500 touch-none">
                        <GripVertical className="w-5 h-5" />
                      </div>
                    )}

                    {/* Card - clickable */}
                    <button
                      onClick={() => navigate(`/chat/${channel.id}`)}
                      className="flex-1 flex items-center gap-3 text-left min-w-0"
                      data-testid={`channel-${channel.id}`}
                    >
                      <div className={`w-10 h-10 rounded-xl ${isDirect ? "bg-indigo-100 text-indigo-600" : style.icon} flex items-center justify-center flex-shrink-0`}>
                        {isDirect ? <User className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-stone-900 truncate text-sm">
                          {title}
                        </h3>
                        <p className="text-xs text-stone-500 truncate">
                          {subtitle}
                        </p>
                      </div>
                    </button>

                    {/* Actions for admin/management (not on direct messages) */}
                    {isManager && !isDirect && (
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button
                          onClick={() => startEdit(channel)}
                          className="p-1.5 rounded-lg text-stone-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Modifica"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(channel.id, channel.name)}
                          className="p-1.5 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <div className="h-20" />

      {showFeedback && (
        <FeedbackModal onClose={() => { localStorage.setItem(`feedback_dismissed_${user?.id}`, "1"); setShowFeedback(false); }} />
      )}
    </div>
  );
};

export default ChannelsList;
